import React, { useEffect } from 'react';
import { Button } from '../../../ui/button';
import { IconSettings, IconCheck } from '@tabler/icons-react';
import { useDrag } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { cn } from '../../../../lib/utils';

const PROGRESS_CONFIG = {
 waiting_to_start: { key: 'recruitment', label: 'Recruitment', suffix: 'onboarded', color: 'bg-accent' },
 recruitment: { key: 'recruitment', label: 'Recruitment', suffix: 'onboarded', color: 'bg-accent' },
 live: { key: 'delivery', label: 'Delivery', suffix: 'posted', color: 'bg-accent' },
 payment_report: { key: 'payment', label: 'Payments', suffix: 'paid', color: 'bg-warning' },
};

const STATUS_LABELS = { not_required: 'N/A', missing: 'Missing', linked: 'Linked', draft: 'Draft', ready: 'Ready', sent: 'Sent', paid_declared: 'Marked paid', partially_received: 'Part received', received: 'Received' };
const statusLabel = (state) => STATUS_LABELS[state] || 'Unavailable';

const CampaignCard = React.memo(({ campaign, onSettingsClick, onClick, stage, isMobile = false, canManageCampaigns = true }) => {
 const [{ isDragging }, dragRef, preview] = useDrag({
 type: 'CAMPAIGN_CARD',
 item: () => ({
 campaignId: campaign._id,
 currentStage: stage,
 campaign
 }),
 collect: (monitor) => ({
 isDragging: monitor.isDragging()
 }),
 canDrag: !isMobile && canManageCampaigns
 });

 useEffect(() => {
 preview(getEmptyImage(), { captureDraggingState: true });
 }, [preview]);

 const metrics = campaign.dashboardMetrics || {};
 const operations = campaign.operationalSummary;
 const brandName = operations?.brandName || campaign.brandId?.name || metrics.brandName || 'Unknown Brand';

 const progressCfg = PROGRESS_CONFIG[stage];
 const hasPaymentProgress = operations?.finance != null
   || Number.isFinite(metrics.paidComplete)
   || Number.isFinite(metrics.paidTotal);

 let complete = 0;
 let total = 0;
 if (progressCfg) {
 if (progressCfg.key === 'recruitment') {
 complete = metrics.recruitmentComplete || 0;
 total = metrics.recruitmentTotal || 0;
 } else if (progressCfg.key === 'delivery') {
 complete = metrics.deliveryComplete || 0;
 total = metrics.deliveryTotal || 0;
 } else if (progressCfg.key === 'payment') {
 complete = operations?.finance?.creators?.paidCount ?? metrics.paidComplete ?? 0;
 total = operations?.finance?.creators?.assignedCount ?? metrics.paidTotal ?? metrics.deliveryTotal ?? 0;
 }
 }

 const progress = total > 0 ? (complete / total) * 100 : 0;
 const isFinished = stage === 'finished';

 return (
 <div
 ref={dragRef}
 className={cn(
 'bg-surface-1 flex-shrink-0 relative',
 canManageCampaigns && !isMobile && 'cursor-grab active:cursor-grabbing',
 'hover:bg-surface-2',
 isDragging && 'opacity-0',
 isMobile ? 'w-full min-h-[160px]' : 'w-full min-w-0 min-h-[180px]',
 )}
 onClick={() => !isDragging && onClick(campaign)}
 >
 <div className={cn('h-full flex flex-col', isMobile ? 'p-3' : 'p-4')}>
 {/* Header */}
 <div className="flex justify-between items-start mb-4">
 <div className="flex-1 min-w-0 pr-2">
 <h6 className="font-semibold text-base leading-tight text-text-primary line-clamp-2 -tracking-tight mb-1">
 <button type="button" aria-label={`Open campaign ${campaign.name}`} className="text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-accent">{campaign.name}</button>
 </h6>
 <span className="text-sm text-text-tertiary tracking-wide truncate block">
 {brandName}
 </span>
 </div>
 {canManageCampaigns && <Button
 variant="ghost"
 size="icon"
 aria-label={`Settings for ${campaign.name}`}
 onClick={(e) => {
 e.stopPropagation();
 onSettingsClick(campaign, e);
 }}
 className={cn('flex-shrink-0 text-text-tertiary', isMobile ? 'h-10 w-10' : 'h-8 w-8')}
 >
 <IconSettings size={isMobile ? 20 : 18} strokeWidth={1.5} />
 </Button>}
 </div>

 {/* Progress section */}
 <div className="flex-1 flex flex-col justify-start" style={{ minHeight: 80 }}>
 {isFinished ? (
 <div className="flex items-center gap-2 text-text-tertiary">
 <IconCheck size={16} strokeWidth={2} />
 <span className="text-sm font-medium">Complete</span>
 </div>
 ) : progressCfg && (progressCfg.key !== 'payment' || hasPaymentProgress) ? (
 <div>
 <div className="flex justify-between items-center mb-2 min-w-0">
 <span className="text-sm font-medium text-text-secondary truncate min-w-0">
 {progressCfg.label}
 </span>
 <span className="text-sm text-text-tertiary tabular-nums flex-shrink-0 ml-2">
 {complete} / {total} {progressCfg.suffix}
 </span>
 </div>
 <div className={cn('w-full overflow-hidden bg-surface-2', isMobile ? 'h-1.5' : 'h-1')}>
 <div
 className={cn('ui-motion-progress h-full', progressCfg.color)}
 style={{ width: `${progress}%` }}
 />
 </div>
 </div>
 ) : null}
 {operations && (
 <div className="mt-3 flex flex-wrap gap-2 text-xs" aria-label="Campaign readiness">
 <span className="border border-glass-border bg-surface-0 px-2 py-1 text-text-secondary">Brief <strong className="text-text-primary">{statusLabel(operations.brief?.state)}</strong></span>
 <span className="border border-glass-border bg-surface-0 px-2 py-1 text-text-secondary">Invoice <strong className="text-text-primary">{statusLabel(operations.finance?.invoice?.state)}</strong></span>
 </div>
 )}
 </div>
 </div>
 </div>
 );
});

CampaignCard.displayName = 'CampaignCard';

export default CampaignCard;
